import {
  useState,
  forwardRef,
  type InputHTMLAttributes,
  type ChangeEventHandler,
  type FocusEventHandler,
  type KeyboardEventHandler,
  type MouseEventHandler,
} from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { Input, Label, cn } from '@atrvd/saas-ui'
import { PasswordStrengthMeter } from './password-strength-meter'

/**
 * Props for the PasswordField component
 * Extends standard input attributes with label, error display, and an
 * optional strength meter for new-password flows.
 *
 * The component supports both controlled and uncontrolled usage:
 *   - Controlled: pass `value` (e.g. via react-hook-form's `watch()`).
 *     Required when you want the strength meter / hide-when-empty toggle to
 *     reflect programmatic resets (`reset()`), since react-hook-form does
 *     not dispatch a synthetic change event when it clears the form.
 *   - Uncontrolled: omit `value`. The component tracks input via composed
 *     onChange. Sufficient for fields without a strength meter where a stale
 *     toggle icon after reset is acceptable.
 */
export type PasswordFieldProps = InputHTMLAttributes<HTMLInputElement> & {
  id: string
  label: string
  error?: string
  /** Render a strength meter below the field once the user starts typing. */
  showStrengthMeter?: boolean
}

/**
 * Password input with inline UX affordances:
 *   - Show/hide toggle (only rendered when the field has a value)
 *   - Caps Lock warning when focused and Caps Lock is active
 *   - Optional strength meter for new-password flows
 *
 * Each instance owns its own visibility / focus / caps-lock state. The current
 * input value is read from props when controlled, otherwise tracked internally.
 * forwardRef passes the registration ref through so the component is
 * compatible with react-hook-form's `register()` spread.
 */
export const PasswordField = forwardRef<HTMLInputElement, PasswordFieldProps>(
  ({ id, label, error, className, showStrengthMeter, ...props }, ref) => {
    const [visible, setVisible] = useState(false)
    // Internal value for uncontrolled usage (no `value` prop). When the
    // consumer passes `value`, that takes precedence — see `currentValue`.
    const [internalValue, setInternalValue] = useState<string>(() => {
      const initial = props.defaultValue ?? ''
      return String(initial)
    })
    const [focused, setFocused] = useState(false)
    const [capsLockOn, setCapsLockOn] = useState(false)

    const isControlled = props.value !== undefined
    const currentValue = isControlled ? String(props.value) : internalValue
    const hasValue = currentValue.length > 0

    // Compose with any onChange supplied by react-hook-form's register() spread.
    const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
      if (!isControlled) setInternalValue(e.currentTarget.value)
      props.onChange?.(e)
    }

    const handleFocus: FocusEventHandler<HTMLInputElement> = (e) => {
      setFocused(true)
      props.onFocus?.(e)
    }

    const handleBlur: FocusEventHandler<HTMLInputElement> = (e) => {
      setFocused(false)
      props.onBlur?.(e)
    }

    const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
      setCapsLockOn(e.getModifierState('CapsLock'))
      props.onKeyDown?.(e)
    }

    const handleKeyUp: KeyboardEventHandler<HTMLInputElement> = (e) => {
      setCapsLockOn(e.getModifierState('CapsLock'))
      props.onKeyUp?.(e)
    }

    // FocusEvent has no getModifierState (DOM spec — it only exists on
    // KeyboardEvent and MouseEvent), so the click-focus path catches Caps
    // Lock here on mousedown, before any keystroke arrives. Tab-focus still
    // resolves via handleKeyDown on the first key, since the focus event
    // itself cannot read the modifier.
    const handleMouseDown: MouseEventHandler<HTMLInputElement> = (e) => {
      setCapsLockOn(e.getModifierState('CapsLock'))
      props.onMouseDown?.(e)
    }

    return (
      <div className="space-y-2">
        <Label htmlFor={id}>{label}</Label>
        <div className="relative">
          <Input
            id={id}
            ref={ref}
            type={visible ? 'text' : 'password'}
            className={cn('pr-10', className)}
            {...props}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            onKeyUp={handleKeyUp}
            onMouseDown={handleMouseDown}
          />
          {hasValue && (
            <button
              type="button"
              // Suppress the default mousedown focus shift so clicking the
              // eye icon while typing does not steal the caret from the
              // input — the standard show/hide-password UX bug. The button
              // stays in the default Tab order (no tabIndex override) so
              // keyboard-only users can reach it and activate via Space /
              // Enter, per WCAG 2.1.1.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setVisible((v) => !v)}
              aria-label={visible ? 'Hide password' : 'Show password'}
              className="absolute right-0 top-0 flex h-10 w-10 items-center justify-center text-muted-foreground hover:text-foreground"
            >
              {visible ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          )}
        </div>
        {focused && capsLockOn && (
          <p className="text-sm text-amber-600" role="status">
            Caps Lock is on
          </p>
        )}
        {showStrengthMeter && hasValue && (
          <PasswordStrengthMeter value={currentValue} />
        )}
        {error && <p className="text-sm text-destructive">{error}</p>}
      </div>
    )
  }
)
PasswordField.displayName = 'PasswordField'
